import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const createMemberIcon = (isCoordinator) => {
  if (isCoordinator) {
    return L.divIcon({
      className: 'coordinator-marker',
      html: `<div style="background-color: #f59e0b; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; font-size: 20px; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4);">🌟</div>`,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -40],
    });
  }
  return L.divIcon({
    className: 'member-marker',
    html: `<div style="background-color: #3b82f6; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; font-size: 16px; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);">👤</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

const BRAND_COLOURS = {
  coles: '#dc2626',
  woolworths: '#16a34a',
  aldi: '#1d4ed8',
};
const BRAND_LABEL = {
  coles: 'Coles',
  woolworths: 'Woolworths',
  aldi: 'Aldi',
};

const createStoreIcon = (brand) => {
  const colour = BRAND_COLOURS[brand] || '#f97316';
  return L.divIcon({
    className: 'store-marker',
    html: `<div style="background-color: ${colour}; border-radius: 50%; width: 22px; height: 22px; display: flex; align-items: center; justify-content: center; font-size: 11px; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);">🛒</div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 22],
    popupAnchor: [0, -22],
  });
};

const createSupplierIcon = (type) => {
  const colour = type === 'hub' ? '#7c3aed' : '#16a34a';
  const emoji = type === 'hub' ? '🏪' : '🚜';
  return L.divIcon({
    className: 'supplier-marker',
    html: `<div style="background-color: ${colour}; border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; font-size: 15px; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);">${emoji}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -30],
  });
};

export default function KinMembersMap({ kinId, kinName, className = '' }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerLayerRef = useRef(null);
  const storeLayerRef = useRef(null);
  const supplierLayerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [cartoApiKey, setCartoApiKey] = useState('');
  const [mapReady, setMapReady] = useState(false);

  // Store layer state
  const [showStores, setShowStores] = useState(false);
  const [stores, setStores] = useState([]);
  const [storesLoaded, setStoresLoaded] = useState(false);
  const [storesLoading, setStoresLoading] = useState(false);

  // Supplier layer state
  const [showSuppliers, setShowSuppliers] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoaded, setSuppliersLoaded] = useState(false);
  const [suppliersLoading, setSuppliersLoading] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch('/api/config/carto-key');
        if (res.ok) {
          const result = await res.json();
          setCartoApiKey(result.key);
        }
      } catch (error) {
        console.error('Failed to fetch Carto key:', error);
      }
    };
    fetchConfig();
  }, []);

  useEffect(() => {
    const fetchMembers = async () => {
      if (!kinId) return;
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/groups/${kinId}/members`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to fetch members');
        const result = await res.json();
        setData(result);
      } catch (err) {
        console.error('Error fetching Kin members:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, [kinId]);

  useEffect(() => {
    const fetchStores = async () => {
      if (!kinId || !showStores || storesLoaded) return;
      setStoresLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/kins/${kinId}/nearby-supermarkets`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to fetch stores');
        const result = await res.json();
        setStores(result.supermarkets || []);
        setStoresLoaded(true);
      } catch (err) {
        console.error('Error fetching stores:', err);
      } finally {
        setStoresLoading(false);
      }
    };
    fetchStores();
  }, [kinId, showStores, storesLoaded]);

  useEffect(() => {
    const fetchSuppliers = async () => {
      if (!kinId || !showSuppliers || suppliersLoaded) return;
      setSuppliersLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/kins/${kinId}/nearby-suppliers`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to fetch suppliers');
        const result = await res.json();
        setSuppliers(result.suppliers || []);
        setSuppliersLoaded(true);
      } catch (err) {
        console.error('Error fetching suppliers:', err);
      } finally {
        setSuppliersLoading(false);
      }
    };
    fetchSuppliers();
  }, [kinId, showSuppliers, suppliersLoaded]);

  // Create map once
  useEffect(() => {
    if (!mapRef.current || !data || !cartoApiKey) return;
    if (mapInstanceRef.current) return;

    const coordinatorLoc = data.coordinator?.location?.coordinates;
    const kinCentre = data.kinCentre?.coordinates;

    let center = [-37.8136, 144.9631];
    if (coordinatorLoc && (coordinatorLoc[0] !== 0 || coordinatorLoc[1] !== 0)) {
      center = [coordinatorLoc[1], coordinatorLoc[0]];
    } else if (kinCentre && (kinCentre[0] !== 0 || kinCentre[1] !== 0)) {
      center = [kinCentre[1], kinCentre[0]];
    }

    const map = L.map(mapRef.current, {
      center,
      zoom: 12,
      zoomControl: true,
      scrollWheelZoom: true,
    });

    const cartoTileUrl = `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=${cartoApiKey}`;
    L.tileLayer(cartoTileUrl, {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; CartoDB',
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(map);

    const radiusKm = data.kinRadius || 30;
    if (coordinatorLoc || kinCentre) {
      const centreCoords = coordinatorLoc || kinCentre;
      L.circle([centreCoords[1], centreCoords[0]], {
        radius: radiusKm * 1000,
        color: '#f59e0b',
        fillColor: '#f59e0b',
        fillOpacity: 0.1,
        weight: 2,
        dashArray: '5, 5',
      }).addTo(map);
    }

    const markerLayer = L.layerGroup().addTo(map);
    markerLayerRef.current = markerLayer;

    storeLayerRef.current = L.layerGroup();
    supplierLayerRef.current = L.layerGroup();

    const allMarkers = [];

    if (data.coordinator && data.coordinator.location?.coordinates) {
      const coords = data.coordinator.location.coordinates;
      if (coords[0] !== 0 || coords[1] !== 0) {
        const pos = [coords[1], coords[0]];
        allMarkers.push(pos);
        const marker = L.marker(pos, { icon: createMemberIcon(true) }).addTo(markerLayer);
        marker.bindPopup(`
          <div style="min-width: 150px;">
            <div style="font-weight: 600; color: #f59e0b;">🌟 ${data.coordinator.name || 'Coordinator'}</div>
            <div style="font-size: 12px; color: #6b7280; margin-top: 4px;">Kin Coordinator</div>
            ${data.coordinator.location?.address ? `<div style="font-size: 11px; color: #9ca3af; margin-top: 4px;">${data.coordinator.location.address}</div>` : ''}
          </div>
        `);
      }
    }

    if (data.members && data.members.length > 0) {
      data.members.forEach((member) => {
        if (member.isCoordinator) return;
        if (member.location?.coordinates) {
          const coords = member.location.coordinates;
          if (coords[0] !== 0 || coords[1] !== 0) {
            const pos = [coords[1], coords[0]];
            allMarkers.push(pos);
            const marker = L.marker(pos, { icon: createMemberIcon(false) }).addTo(markerLayer);
            marker.bindPopup(`
              <div style="min-width: 150px;">
                <div style="font-weight: 600; color: #3b82f6;">👤 ${member.name || 'Member'}</div>
                <div style="font-size: 12px; color: #6b7280; margin-top: 4px;">Kin Member</div>
                ${member.location?.address ? `<div style="font-size: 11px; color: #9ca3af; margin-top: 4px;">${member.location.address}</div>` : ''}
              </div>
            `);
          }
        }
      });
    }

    if (allMarkers.length > 1) {
      const bounds = L.latLngBounds(allMarkers);
      map.fitBounds(bounds, { padding: [50, 50] });
    } else if (allMarkers.length === 1) {
      map.setView(allMarkers[0], 12);
    }

    mapInstanceRef.current = map;
    setMapReady(true);
  }, [data, cartoApiKey]);

  // Store layer update
  useEffect(() => {
    if (!mapInstanceRef.current || !storeLayerRef.current || !mapReady) return;

    const map = mapInstanceRef.current;
    const layer = storeLayerRef.current;
    layer.clearLayers();

    if (showStores && stores.length > 0) {
      stores.forEach((s) => {
        const pos = [s.latitude, s.longitude];
        const marker = L.marker(pos, { icon: createStoreIcon(s.brand) });
        const brandLabel = BRAND_LABEL[s.brand] || s.brand;
        marker.bindPopup(`
          <div style="min-width: 180px;">
            <div style="font-weight: 600; color: ${BRAND_COLOURS[s.brand] || '#f97316'};">🛒 ${s.name || brandLabel}</div>
            <div style="font-size: 12px; color: #6b7280; margin-top: 4px;">${brandLabel} store</div>
            ${s.address ? `<div style="font-size: 11px; color: #9ca3af; margin-top: 4px;">${[s.address.suburb, s.address.state, s.address.postcode].filter(Boolean).join(', ')}</div>` : ''}
            <div style="font-size: 11px; color: #9ca3af; margin-top: 4px;">${s.distanceKm} km from Kin</div>
          </div>
        `);
        marker.addTo(layer);
      });
      layer.addTo(map);
    } else {
      if (map.hasLayer(layer)) map.removeLayer(layer);
    }
  }, [showStores, stores, mapReady]);

  // Supplier layer update
  useEffect(() => {
    if (!mapInstanceRef.current || !supplierLayerRef.current || !mapReady) return;

    const map = mapInstanceRef.current;
    const layer = supplierLayerRef.current;
    layer.clearLayers();

    if (showSuppliers && suppliers.length > 0) {
      suppliers.forEach((s) => {
        const pos = [s.latitude, s.longitude];
        const marker = L.marker(pos, { icon: createSupplierIcon(s.type) });
        marker.bindPopup(`
          <div style="min-width: 180px;">
            <div style="font-weight: 600; color: ${s.type === 'hub' ? '#7c3aed' : '#16a34a'};">
              ${s.type === 'hub' ? '🏪' : '🚜'} ${s.name}
            </div>
            <div style="font-size: 12px; color: #6b7280; margin-top: 4px;">
              ${s.type === 'hub' ? 'Hub' : 'Producer'}
            </div>
            ${s.suburb ? `<div style="font-size: 11px; color: #9ca3af; margin-top: 4px;">${[s.suburb, s.state, s.postcode].filter(Boolean).join(', ')}</div>` : ''}
            <div style="font-size: 11px; color: #9ca3af; margin-top: 4px;">${s.distanceKm} km from Kin · ${s.productCount} product${s.productCount !== 1 ? 's' : ''}</div>
          </div>
        `);
        marker.addTo(layer);
      });
      layer.addTo(map);
    } else {
      if (map.hasLayer(layer)) map.removeLayer(layer);
    }
  }, [showSuppliers, suppliers, mapReady]);

  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-[500px] bg-gray-50 rounded-lg ${className}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading members map...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center h-[500px] bg-red-50 rounded-lg ${className}`}>
        <div className="text-center text-red-600">
          <p>Error loading map: {error}</p>
        </div>
      </div>
    );
  }

  const membersWithLocation = data?.members?.filter(m =>
    m.location?.coordinates &&
    (m.location.coordinates[0] !== 0 || m.location.coordinates[1] !== 0)
  ) || [];

  const coordinatorHasLocation = data?.coordinator?.location?.coordinates &&
    (data.coordinator.location.coordinates[0] !== 0 || data.coordinator.location.coordinates[1] !== 0);

  const storeBrandCounts = {};
  stores.forEach((s) => {
    storeBrandCounts[s.brand] = (storeBrandCounts[s.brand] || 0) + 1;
  });

  const supplierTypeCounts = {
    producer: suppliers.filter((s) => s.type === 'producer').length,
    hub: suppliers.filter((s) => s.type === 'hub').length,
  };

  return (
    <div className={className}>
      <div
        ref={mapRef}
        className="rounded-lg overflow-hidden shadow-lg bg-gray-100"
        style={{ height: '500px', width: '100%', minHeight: '500px' }}
      />

      <div className="mt-4 flex flex-wrap gap-4 text-sm items-center">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🌟</span>
          <span>Kin Coordinator {coordinatorHasLocation ? '📍' : '(no location)'}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-2xl">👤</span>
          <span>Member ({membersWithLocation.length})</span>
        </div>
        {data?.members && data.members.length > membersWithLocation.length && (
          <div className="flex items-center gap-2 text-gray-500">
            <span className="text-2xl">⚠️</span>
            <span>{data.members.length - membersWithLocation.length} without location</span>
          </div>
        )}

        <label className="flex items-center gap-2 ml-auto cursor-pointer bg-gray-50 px-3 py-1.5 rounded-lg border">
          <input
            type="checkbox"
            checked={showSuppliers}
            onChange={(e) => setShowSuppliers(e.target.checked)}
            className="w-4 h-4"
          />
          <span>🚜 Suppliers</span>
          {suppliersLoading && <span className="text-xs text-gray-500">loading...</span>}
          {!suppliersLoading && suppliersLoaded && suppliers.length > 0 && (
            <span className="text-xs text-gray-500">({suppliers.length})</span>
          )}
        </label>

        <label className="flex items-center gap-2 cursor-pointer bg-gray-50 px-3 py-1.5 rounded-lg border">
          <input
            type="checkbox"
            checked={showStores}
            onChange={(e) => setShowStores(e.target.checked)}
            className="w-4 h-4"
          />
          <span>🛒 Vhub stores</span>
          {storesLoading && <span className="text-xs text-gray-500">loading...</span>}
          {!storesLoading && storesLoaded && stores.length > 0 && (
            <span className="text-xs text-gray-500">({stores.length})</span>
          )}
        </label>
      </div>

      {showSuppliers && suppliersLoaded && suppliers.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-600">
          {supplierTypeCounts.producer > 0 && (
            <div className="flex items-center gap-1">
              <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#16a34a' }} />
              <span>Producers ({supplierTypeCounts.producer})</span>
            </div>
          )}
          {supplierTypeCounts.hub > 0 && (
            <div className="flex items-center gap-1">
              <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#7c3aed' }} />
              <span>Hubs ({supplierTypeCounts.hub})</span>
            </div>
          )}
          <span className="text-gray-400">within 30 km</span>
        </div>
      )}

      {showStores && storesLoaded && stores.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-600">
          {Object.entries(BRAND_LABEL).map(([brand, label]) => {
            const count = storeBrandCounts[brand] || 0;
            if (count === 0) return null;
            return (
              <div key={brand} className="flex items-center gap-1">
                <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: BRAND_COLOURS[brand] }} />
                <span>{label} ({count})</span>
              </div>
            );
          })}
          <span className="text-gray-400">within 15 km</span>
        </div>
      )}

      {data?.members && data.members.length > 0 && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs font-medium text-gray-600 mb-2">
            👥 {data.members.length} member{data.members.length !== 1 ? 's' : ''} in {kinName || 'this Kin'}
          </p>
          <div className="flex flex-wrap gap-2">
            {data.members.map((member) => (
              <span
                key={member._id}
                className={`text-xs px-2 py-1 rounded-full ${
                  member.isCoordinator
                    ? 'bg-amber-100 text-amber-800'
                    : member.location?.coordinates && (member.location.coordinates[0] !== 0 || member.location.coordinates[1] !== 0)
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-200 text-gray-500'
                }`}
                title={member.location?.address || 'No location set'}
              >
                {member.isCoordinator ? '🌟' : '👤'} {member.name}
                {!member.isCoordinator && (!member.location?.coordinates || (member.location.coordinates[0] === 0 && member.location.coordinates[1] === 0)) && ' (no location)'}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
